<template>
  <div class="pa-3">
    <v-progress-linear v-if="permissionsLoading || loading" indeterminate color="primary" />

    <v-alert v-if="permissionsError" type="error" outlined>
      {{ $t('expedienteChecklists.permissionsError') }}
      <v-btn text @click="loadPermissions">{{ $t('expedienteChecklists.retry') }}</v-btn>
    </v-alert>
    <v-alert v-else-if="!permissionsLoading && !canList" type="warning" outlined>
      {{ $t('expedienteChecklists.noPermission') }}
    </v-alert>

    <template v-else-if="canList">
      <v-alert v-if="!iteracion.uuid" type="info" outlined>
        {{ $t('expedienteChecklists.selectIteration') }}
      </v-alert>
      <v-alert v-else-if="error" type="error" outlined>
        {{ $t('expedienteChecklists.loadError') }}
        <v-btn text @click="loadAuditorias(page)">{{ $t('expedienteChecklists.retry') }}</v-btn>
      </v-alert>
      <v-alert v-else-if="!loading && groups.length === 0" type="info" outlined>
        {{ $t('expedienteChecklists.noAudits') }}
      </v-alert>

      <template v-else-if="!loading">
        <p>{{ $t('expedienteChecklists.auditsCount', { count: total }) }}</p>
        <v-expansion-panels v-model="expanded" multiple>
          <v-expansion-panel v-for="group in groups" :key="group.auditoria.uuid">
            <v-expansion-panel-header>
              <div>
                <strong>{{ $t('expedienteChecklists.audit') }} {{ group.auditoria.codigo }}</strong>
                <span v-if="group.auditoria.desde" class="ml-3">
                  {{ $moment(group.auditoria.desde).locale($i18n.locale).format('L') }}
                </span>
                <v-chip v-if="group.auditoria.estado" small outlined class="ml-3">
                  {{ $t('estados.' + group.auditoria.estado.replace(/ /g, '_')) }}
                </v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-progress-linear v-if="group.loading" indeterminate color="primary" />
              <v-alert v-else-if="group.error" type="error" outlined>
                {{ $t('expedienteChecklists.checklistsError') }}
                <v-btn text @click="loadChecklists(group, group.page)">
                  {{ $t('expedienteChecklists.retry') }}
                </v-btn>
              </v-alert>
              <template v-else-if="group.loaded">
                <v-alert v-if="group.checklists.length === 0" type="info" outlined>
                  {{ $t('expedienteChecklists.noChecklists') }}
                </v-alert>
                <template v-else>
                  <p class="mx-4 mt-2">{{ $t('expedienteChecklists.checklistsCount', { count: group.total }) }}</p>
                  <v-simple-table>
                    <thead>
                      <tr>
                        <th>{{ $t('expedienteChecklists.name') }}</th>
                        <th>{{ $t('expedienteChecklists.auditor') }}</th>
                        <th>{{ $t('expedienteChecklists.site') }}</th>
                        <th>{{ $t('expedienteChecklists.date') }}</th>
                        <th>{{ $t('expedienteChecklists.actions') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="checklist in group.checklists" :key="checklist.uuid">
                        <td>
                          {{ checklist.nombre }}
                          <div v-if="checklist.descripcion" class="caption">{{ checklist.descripcion }}</div>
                        </td>
                        <td><span v-if="checklist.auditor">{{ checklist.auditor.nombreCompleto }}</span></td>
                        <td><span v-if="checklist.emplazamiento">{{ checklist.emplazamiento.nombre }}</span></td>
                        <td>
                          <span v-if="checklist.fecha">
                            {{ $moment(checklist.fecha).locale($i18n.locale).format('L') }}
                          </span>
                        </td>
                        <td>
                          <v-btn small text color="primary" :to="checklistRoute(group, checklist)">
                            {{ $t('expedienteChecklists.open') }}
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </template>
                <v-pagination
                  v-if="group.view && group.view['hydra:last']"
                  :value="group.page"
                  :length="pageCount(group.view)"
                  :aria-label="$t('expedienteChecklists.checklistPages')"
                  class="mt-3"
                  @input="loadChecklists(group, $event)" />
              </template>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-pagination
          v-if="view && view['hydra:last']"
          :value="page"
          :length="pageCount(view)"
          :aria-label="$t('expedienteChecklists.auditPages')"
          class="mt-4"
          @input="loadAuditorias($event)" />
      </template>
    </template>
  </div>
</template>

<script>
export default {
  props: {
    iteracion: { type: Object, required: true },
  },
  data: () => ({
    permissionsLoading: true,
    permissionsError: false,
    auditAuth: {},
    checklistAuth: {},
    loading: false,
    error: false,
    groups: [],
    expanded: [],
    page: 1,
    total: 0,
    view: null,
    requestId: 0,
    disposed: false,
  }),
  computed: {
    canList() {
      return Boolean(this.auditAuth.canList && this.checklistAuth.canList)
    },
  },
  watch: {
    'iteracion.uuid'() {
      this.loadAuditorias()
    },
    expanded(indices) {
      for (const index of indices) {
        const group = this.groups[index]
        if (group && !group.loaded && !group.loading && !group.error) this.loadChecklists(group)
      }
    },
  },
  created() {
    this.loadPermissions()
  },
  beforeDestroy() {
    this.disposed = true
    this.requestId++
  },
  methods: {
    async loadPermissions() {
      this.permissionsLoading = true
      this.permissionsError = false
      try {
        const [auditAuth, checklistAuth] = await Promise.all([
          this.$store.dispatch('user/can', { module: 'auditorias', return: 'all' }),
          this.$store.dispatch('user/can', { module: 'checklists', return: 'all' }),
        ])
        if (this.disposed) return
        this.auditAuth = auditAuth
        this.checklistAuth = checklistAuth
      } catch (error) {
        if (!this.disposed) this.permissionsError = true
      } finally {
        this.permissionsLoading = false
      }
      if (!this.disposed && !this.permissionsError) await this.loadAuditorias()
    },
    async loadAuditorias(page = 1) {
      const requestId = ++this.requestId
      const iteracionUuid = this.iteracion.uuid
      this.groups = []
      this.expanded = []
      this.view = null
      this.total = 0
      this.page = page
      this.error = false
      this.loading = false
      if (this.disposed || this.permissionsLoading || !this.canList || !iteracionUuid) return

      this.loading = true
      try {
        const data = await this.$store.dispatch('auditorias/listPageByIteracion', { iteracionUuid, page })
        if (this.requestId !== requestId || this.iteracion.uuid !== iteracionUuid) return
        this.groups = data['hydra:member'].map(auditoria => ({
          auditoria,
          checklists: [],
          loaded: false,
          loading: false,
          error: false,
          page: 1,
          total: 0,
          view: null,
          requestId: 0,
        }))
        this.total = data['hydra:totalItems']
        this.view = data['hydra:view']
        if (this.groups.length) this.expanded = [0]
      } catch (error) {
        if (this.requestId === requestId && this.iteracion.uuid === iteracionUuid) this.error = true
      } finally {
        if (this.requestId === requestId) this.loading = false
      }
    },
    async loadChecklists(group, page = 1) {
      if (this.disposed || !this.canList || !this.groups.includes(group)) return
      const requestId = ++group.requestId
      const iteracionUuid = this.iteracion.uuid
      group.checklists = []
      group.view = null
      group.page = page
      group.loaded = false
      group.error = false
      group.loading = true
      const isCurrent = () => !this.disposed && this.iteracion.uuid === iteracionUuid &&
        this.groups.includes(group) && group.requestId === requestId

      try {
        const data = await this.$store.dispatch('checklists/listPageByAuditoria', {
          auditoriaUuid: group.auditoria.uuid,
          page,
        })
        if (!isCurrent()) return
        group.checklists = data['hydra:member']
        group.total = data['hydra:totalItems']
        group.view = data['hydra:view']
        group.loaded = true
      } catch (error) {
        if (isCurrent()) group.error = true
      } finally {
        if (isCurrent()) group.loading = false
      }
    },
    pageCount(view) {
      return Number(new URLSearchParams(view['hydra:last'].split('?')[1]).get('page'))
    },
    checklistRoute(group, checklist) {
      return {
        path: `/auditorias/tabs/checklist/${checklist.uuid}`,
        query: { auditoria_uuid: group.auditoria.uuid },
      }
    },
  },
}
</script>
